import { supabase } from '../lib/supabase';
import type { EntryItem, MealType } from './entries';
import { resolveFavorites, type LinkSource } from '../lib/favLink';

export interface Favorite {
  id: string;
  name: string;
  items: EntryItem[];
  meal_type: MealType | null;
  calories: number;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
}

export interface NewFavorite {
  name: string;
  items: EntryItem[];
  meal_type: MealType | null;
  calories: number;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
}

/** פריט מועדף → פריט ליומן: בלי `aliases` (שמות לסידור הקבועים — שייכים למועדף, לא לרישום). */
export function favItemForLog({ aliases: _aliases, ...it }: EntryItem): EntryItem {
  return it;
}

export async function fetchFavorites(): Promise<Favorite[]> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('no_session');
  const { data, error } = await supabase
    .from('litebite_favorites')
    .select('id, name, items, meal_type, calories, protein_g, carbs_g, fat_g')
    .eq('user_id', session.user.id)   // הכלל של שכבת הנתונים: סינון מפורש, לא סמיכה על RLS
    .order('created_at', { ascending: false });
  if (error) throw error;
  const favs = (data ?? []) as Favorite[];
  // מועדף = קישור למאכל: השם והערכים מגיעים מהמאכל העדכני (lib/favLink.ts). כשל בשליפת המאכלים לא
  // מפיל את המועדפים — הם מוצגים כפי שנשמרו.
  try {
    const { customs, national } = await fetchLinkSources(session.user.id, favs);
    return resolveFavorites(favs, customs, national);
  } catch {
    return favs;
  }
}

const toSource = (r: Record<string, unknown>): LinkSource => ({
  name_he: String(r.name_he),
  calories: Number(r.calories),
  protein_g: r.protein_g === null ? null : Number(r.protein_g),
  carbs_g: r.carbs_g === null ? null : Number(r.carbs_g),
  fat_g: r.fat_g === null ? null : Number(r.fat_g),
  portions: Array.isArray(r.portions) ? (r.portions as LinkSource['portions']) : [],
  icon: (r.icon as string | null) ?? null,
});

/** המאכלים שהמועדפים מקושרים אליהם: כל המאכלים האישיים שלה (גם לקישור לפי שם) + מהמאגר לפי הקודים שבשימוש */
async function fetchLinkSources(userId: string, favs: Favorite[]): Promise<{
  customs: Map<string, LinkSource & { id: string }>; national: Map<number, LinkSource>;
}> {
  const codes = [...new Set(favs.flatMap((f) => (f.items ?? [])
    .map((i) => i.foodCode).filter((c): c is number => typeof c === 'number')))];
  const [cq, nq] = await Promise.all([
    supabase.from('litebite_custom_foods')
      .select('id, name_he, calories, protein_g, carbs_g, fat_g, portions, icon')
      .eq('user_id', userId),   // למלווה יש SELECT גם על מאכלים של מי ששיתף איתו
    codes.length
      ? supabase.from('litebite_foods').select('code, name_he, calories, protein_g, carbs_g, fat_g, portions, icon').in('code', codes)
      : Promise.resolve({ data: [], error: null }),
  ]);
  if (cq.error) throw cq.error;
  if (nq.error) throw nq.error;
  const customs = new Map((cq.data ?? []).map((r) => [String(r.id), { ...toSource(r), id: String(r.id) }]));
  const national = new Map((nq.data ?? []).map((r) => [Number(r.code), toSource(r)]));
  return { customs, national };
}

/** מחזיר את ה-id החדש — כדי שאפשר יהיה להסיר מיד בלי לרענן את רשימת המועדפים */
export async function addFavorite(fav: NewFavorite): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('no_session');
  const { data, error } = await supabase.from('litebite_favorites')
    .insert({ ...fav, user_id: session.user.id }).select('id').single();
  if (error) throw error;
  return (data as { id: string }).id;
}

export async function removeFavorite(id: string): Promise<void> {
  const { error } = await supabase.from('litebite_favorites').delete().eq('id', id);
  if (error) throw error;
}
